import { useEffect, useState } from 'react';

/**
 * Cores resolvidas a partir dos tokens CSS (globals.css) para uso no recharts.
 *
 * O recharts renderiza `stroke`/`fill` como atributos SVG, onde `var(--token)`
 * NÃO resolve. Então lemos os valores computados das CSS vars e re-renderizamos
 * quando o tema muda (classe `.dark` no <html>), cobrindo tanto o toggle
 * explícito quanto o tema 'system'.
 */
export interface ChartColors {
  primary: string;
  tertiary: string;
  error: string;
  warning: string;
  info: string;
  secondary: string;
  grid: string;
  axis: string;
  tooltipBg: string;
  tooltipBorder: string;
  textTitle: string;
  textBody: string;
}

const readVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const rgb = (name: string, alpha?: number): string => {
  const channels = readVar(name);
  if (!channels) return 'transparent';
  return alpha == null ? `rgb(${channels})` : `rgb(${channels} / ${alpha})`;
};

const readColors = (): ChartColors => ({
  primary: rgb('--primary'),
  tertiary: rgb('--tertiary'),
  error: rgb('--error'),
  warning: rgb('--warning'),
  info: rgb('--info'),
  secondary: rgb('--secondary'),
  grid: rgb('--border', 0.45),
  axis: rgb('--text-secondary'),
  tooltipBg: rgb('--bg-elevated'),
  tooltipBorder: rgb('--border'),
  textTitle: rgb('--text-title'),
  textBody: rgb('--text-body'),
});

export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(readColors);

  useEffect(() => {
    const update = () => setColors(readColors());
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return colors;
}
